/**
 * Case-insensitive folder-name glob matching (A2F-1035 / FRE).
 *
 * Mirrors the backend's matching model in `backend/services/folder_rules.py`
 * (`glob_match` → `fnmatch.fnmatchcase(name.casefold(), pattern.casefold())`),
 * so "what the FRE discovery list marks as ignored" exactly matches what
 * ingest will skip server-side.
 *
 * Semantics (case-insensitive, full-string anchored):
 *   - A no-wildcard pattern is a case-insensitive EXACT match:
 *     `Drafts` matches `Drafts` / `drafts`, NOT `Drafts v2`.
 *   - `*` matches any run of characters (including empty):
 *     `Final*` = startswith, `*Final` = endswith, `*x*` = contains.
 *   - `?` matches exactly one character.
 *   - Every other regex metacharacter is escaped, so it matches literally
 *     (`PeC (old)` matches only the literal folder name `PeC (old)`).
 *
 * Divergence from Python `fnmatch`: we deliberately do NOT implement `[seq]`
 * character classes — a literal `[` in a chip matches a literal `[`. Folder
 * names users type as chips are plain names with at most `*`/`?` wildcards,
 * never bracket expressions, and the backend's seed/default globs use only
 * `*`. Keeping the surface to `*`/`?` matches the UI hint text shown next to
 * the FolderTagInput.
 */

/** Convert a single case-insensitive glob into an anchored RegExp. */
function globToRegExp(pattern: string): RegExp {
  let out = '';
  for (const ch of pattern) {
    if (ch === '*') {
      out += '.*';
    } else if (ch === '?') {
      out += '.';
    } else {
      // Escape any regex metacharacter so it matches literally.
      out += ch.replace(/[.+^${}()|[\]\\]/g, '\\$&');
    }
  }
  // Full-string anchored + case-insensitive (filesystem is case-insensitive,
  // matching the backend's casefold on both sides).
  return new RegExp(`^${out}$`, 'i');
}

/**
 * Return true if folder `name` matches `pattern` under the case-insensitive
 * glob model above. A blank name or blank pattern never matches (defensive —
 * an empty chip must not match everything, mirroring the backend).
 */
export function globMatch(name: string, pattern: string): boolean {
  const n = (name ?? '').trim();
  const p = (pattern ?? '').trim();
  if (!n || !p) return false;
  return globToRegExp(p).test(n);
}

/**
 * Return true if `name` matches ANY of `patterns`. Convenience used by the FRE
 * discovery list to test a folder against the effective ignore-glob set
 * (systemCore ∪ the user's current ignore chips).
 */
export function globMatchAny(name: string, patterns: readonly string[]): boolean {
  return patterns.some((p) => globMatch(name, p));
}
